import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import WorkContentLayout from '@/components/Approval/@components/WorkContentLayout';
import ApprovalHeader from '@/components/Approval/@components/ApprovalHeader';
import {
  ApprovalFilter,
  ApprovalFilterType,
  ApprovalList,
  SearchData,
  TotalApprovalParams,
} from '@/types/api';
import PaginationBar from '@/components/PaginationBar';
import dayjs from 'dayjs';
import FilterBar from '@/components/Approval/RightContainer/FilterBar';
import ApprovalSort from '@/components/Approval/RightContainer/FilterBar/ApprovalSort';
import { useAtom, useAtomValue } from 'jotai';
import { approvalFilter, MyInfoState } from '@/recoil/atom';
import NoReadButton from '@/components/Approval/@components/NoReadButton';
import useHandleScroll from '@/components/Approval/Content/hooks/useHandleScroll';
import { DashIcon } from '@/theme/overrides/CustomIcons';
import {
  BreadcrumbChild,
  BreadcrumbClickable,
  BreadScrumTitle,
} from '@/components/Approval/Content/DocumentContents/styles';
import { ListContainer } from '@/components/Approval/RightContainer/styles';
import NoData from '@/components/Approval/@components/NoData';
import Loading from '@/components/Approval/RightContainer/ApprovalContent/Loading';
import ApprovalContent from '@/components/Approval/RightContainer/ApprovalContent';
import { formatDayjsToDateString, getFormattedToday } from '@/utils/format';

// 간단한 breadcrumb 매핑
const BREADCRUMB_CONFIG = {
  // 자식 -> 부모 관계
  children: {
    [ApprovalFilterType['결재할 문서']]: ApprovalFilterType['내가 결재자인'],
    [ApprovalFilterType['결재한 문서']]: ApprovalFilterType['내가 결재자인'],
    [ApprovalFilterType['협조결재한 문서']]:
      ApprovalFilterType['내가 결재자인'],
    [ApprovalFilterType['진행중 문서']]: ApprovalFilterType['내가 작성자인'],
    [ApprovalFilterType['임시저장 문서']]: ApprovalFilterType['내가 작성자인'],
    [ApprovalFilterType['완료 문서']]: ApprovalFilterType['내가 작성자인'],
    [ApprovalFilterType['반려 문서']]: ApprovalFilterType['내가 작성자인'],
  } as Partial<Record<ApprovalFilterType, ApprovalFilterType>>,
  // ID -> 이름
  names: {
    [ApprovalFilterType['즐겨찾기']]: '즐겨찾기',
    [ApprovalFilterType['내 필터']]: '내 필터',
    [ApprovalFilterType['전체 문서']]: '전체 문서',
    [ApprovalFilterType['내가 결재자인']]: '내가 결재자인',
    [ApprovalFilterType['결재할 문서']]: '결재할 문서',
    [ApprovalFilterType['결재한 문서']]: '결재한 문서',
    [ApprovalFilterType['협조결재한 문서']]: '협조결재한 문서',
    [ApprovalFilterType['내가 작성자인']]: '내가 작성자인',
    [ApprovalFilterType['진행중 문서']]: '진행중 문서',
    [ApprovalFilterType['임시저장 문서']]: '임시저장 문서',
    [ApprovalFilterType['완료 문서']]: '완료 문서',
    [ApprovalFilterType['반려 문서']]: '반려 문서',
    [ApprovalFilterType['내가 참조자인 문서']]: '내가 참조자인 문서',
    [ApprovalFilterType['팀원의 작성 문서']]: '팀원의 작성 문서',
    [ApprovalFilterType['팀원의 협조결재 문서']]: '팀원의 협조결재 문서',
  },
};

const getApprovalBreadcrumb = (
  id: ApprovalFilterType | string,
  t: (key: string) => string,
  filterName?: string,
  onCategoryClick?: (id: number) => void,
) => {
  // 커스텀 필터명이 있는 경우
  if (filterName) {
    return (
      <>
        <BreadScrumTitle>Approval</BreadScrumTitle>
        <DashIcon style={{ margin: '8px' }} />
        <BreadcrumbChild>{filterName}</BreadcrumbChild>
      </>
    );
  }

  const numericId = typeof id === 'string' ? parseInt(id) : id;
  const parentId = BREADCRUMB_CONFIG.children[numericId as ApprovalFilterType];

  const currentName = t(
    BREADCRUMB_CONFIG.names[numericId as ApprovalFilterType] || '전체 문서',
  );

  // 부모가 있고 클릭 핸들러가 있는 경우
  if (parentId && onCategoryClick) {
    const parentName = t(BREADCRUMB_CONFIG.names[parentId]);
    return (
      <>
        <BreadScrumTitle>Approval</BreadScrumTitle>
        <DashIcon style={{ margin: '8px' }} />
        <BreadcrumbClickable onClick={() => onCategoryClick(parentId)}>
          {parentName}
        </BreadcrumbClickable>
        <DashIcon style={{ margin: '8px' }} />
        <BreadcrumbChild>{currentName}</BreadcrumbChild>
      </>
    );
  }

  // 기본 케이스
  return (
    <>
      <BreadScrumTitle>Approval</BreadScrumTitle>
      <DashIcon style={{ margin: '8px' }} />
      <BreadcrumbChild>{currentName}</BreadcrumbChild>
    </>
  );
};

export const localFilterToParam = (
  filter: ApprovalFilter,
): TotalApprovalParams => {
  const period = filter.endAuthoredAt?.[0];
  const now = getFormattedToday();
  let start: string | undefined;
  let end: string | undefined;

  if (period) {
    switch (period.type) {
      case 0:
        start = formatDayjsToDateString(dayjs().subtract(30, 'day'));
        break;
      case 1:
        start = formatDayjsToDateString(dayjs().subtract(180, 'day'));
        break;
      case 2:
        start = formatDayjsToDateString(dayjs().subtract(1, 'year'));
        break;
      case 3:
        start = period.value;
        end = period.value2;
        break;
    }
    if (period.type !== 3) {
      end = now;
    }
  }

  const mapPassportIds = (arr: { passportId: string }[]) =>
    arr.map((res) => res.passportId);

  return {
    sortType: filter.sortType,
    types: filter.types.map((res) => res.type.toString()),
    authorPassportIds: mapPassportIds(filter.authorPassportIds),
    approverPassportIds: mapPassportIds(filter.approverPassportIds),
    approverStatuses: filter.approverStatuses.join(','),
    currentApproverPassportIds: mapPassportIds(
      filter.currentApproverPassportIds,
    ),
    coApproverPassportIds: mapPassportIds(filter.coApproverPassportIds),
    coApproverStatuses: filter.coApproverStatuses.join(','),
    statuses: filter.statuses.length
      ? filter.statuses.map((res) => res.value)
      : undefined,
    referrerPassportIds: mapPassportIds(filter.referrerPassportIds),
    unreadPassportIds: filter.unreadPassportIds.length
      ? mapPassportIds(filter.unreadPassportIds)
      : undefined,
    finalApproverPassportIds: filter.finalApproverPassportIds?.length
      ? mapPassportIds(filter.finalApproverPassportIds)
      : undefined,
    startAuthoredAt: start,
    endAuthoredAt: end,
    isBookmark: filter.isBookmark,
    authorDepartmentIds: filter.authorDepartmentIds,
    approverDepartmentIds: filter.approverDepartmentIds,
    requireAllApprovers: filter.requireAllApprovers,
    requireAllCoApprovers: filter.requireAllCoApprovers,
  };
};

type RightContainerProps = {
  data?: ApprovalList;
  currentPage: number;
  handleClickPage(pageNum: number): void;
  handleApprovalOpen(docId: string, approvalType?: string): void;
  handleBookMark(docId: string): void;
  onSubmit: ({ keyword, searchFields }: SearchData) => void;
  handleCategoryClick?: (id: number) => void;
};

const RightContainer = ({
  data,
  currentPage,
  handleClickPage,
  handleApprovalOpen,
  handleBookMark,
  onSubmit,
  handleCategoryClick,
}: RightContainerProps) => {
  const { t } = useTranslation();
  const myInfo = useAtomValue(MyInfoState);
  const ref = useRef<HTMLDivElement | null>(null);
  const [filters, setFilters] = useAtom(approvalFilter);

  const { showButton, isTablet, scrollToTop } = useHandleScroll(ref);

  useEffect(() => {
    const currentRef = (ref as React.RefObject<HTMLDivElement | null>).current;
    currentRef?.scrollTo({ top: 0 });
  }, [filters, ref]);

  const handleChange = () => {
    filters.unreadPassportIds.length > 0
      ? setFilters({ ...filters, unreadPassportIds: [] })
      : setFilters({ ...filters, unreadPassportIds: [{ ...myInfo }] });
  };

  return (
    <WorkContentLayout
      ref={ref}
      showTopButton={showButton}
      onScrollToTop={scrollToTop}
    >
      <ApprovalHeader
        breadcrumb={getApprovalBreadcrumb(
          filters.id,
          t,
          filters.filterName,
          handleCategoryClick,
        )}
        totalCount={data?.meta.totalCount}
        isSearching={
          !!(filters.keyword || filters.titleKeyword || filters.contentKeyword)
        }
        leftButton={
          filters.referrerPassportIds.length > 0 && (
            <NoReadButton
              title={t('안읽은 참조 문서만 조회')}
              isChecked={filters.unreadPassportIds.length > 0}
              onChange={handleChange}
            />
          )
        }
        filterComponent={<FilterBar isTablet={isTablet} onSubmit={onSubmit} />}
        searchComponent={<ApprovalSort />}
        isTablet={isTablet}
      />

      <ListContainer>
        {data?.data ? (
          data?.data.length > 0 ? (
            data?.data.map((res) => (
              <ApprovalContent
                key={res.docId}
                {...res}
                handleOpen={handleApprovalOpen}
                handleBookMark={handleBookMark}
              />
            ))
          ) : (
            <NoData />
          )
        ) : (
          <Loading />
        )}
        {data?.meta.endPage && (
          <PaginationBar
            isFixed={false}
            isSticky={true}
            currentPage={currentPage}
            totalPage={data?.meta.endPage}
            handleClick={(num: number) => {
              handleClickPage(num);
              scrollToTop();
            }}
          />
        )}
      </ListContainer>
    </WorkContentLayout>
  );
};

export default RightContainer;
